import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import * as XLSX from 'xlsx';
import { LocationItem, LocationService } from '../core/services/location.service';
import {
  ActivityReportRow,
  ActivityReportService,
} from '../core/services/activity-report.service';
import {
  buildReportDownloadSuffix,
  formatReportDateDisplay,
  formatReportDatePickerLabel,
  openNativeDatePicker,
  resolveReportDateRange,
  toReportDateInputValue,
} from '../shared/utils/report-date-range';

export type ActivityReportMode = 'booked' | 'delivered';

@Component({
  selector: 'activity-report-management-component',
  standalone: true,
  templateUrl: './activity-report-management.component.html',
  styleUrls: ['./activity-report-management.component.css'],
  imports: [CommonModule, FormsModule, RouterModule],
})
export class ActivityReportManagementComponent implements OnInit {
  isInitialLoading = true;
  isRefreshing = false;
  errorMessage: string | null = null;
  isFilterPanelOpen = false;

  /** Figma: Booked (no Provided by) vs Delivered (includes Provided by) */
  reportMode: ActivityReportMode = 'booked';

  centers: LocationItem[] = [];
  filterCenterName = '';
  filterActivityName = '';
  filterActivityType = '';
  filterProvidedBy = '';
  activityOptions: string[] = [];
  activityTypeOptions: string[] = [];
  providedByOptions: string[] = [];
  /** Figma: toolbar uses custom date range only (no Monthly / Year / Month). */
  private readonly timeframe = 'custom' as const;

  /** Draft filters in the Figma side panel (applied on APPLY). */
  draftActivityName = '';
  draftActivityType = '';
  draftProvidedBy = '';
  draftCenterName = '';
  draftFromDate = '';
  draftToDate = '';

  customFromDate = '';
  customToDate = '';

  rows: ActivityReportRow[] = [];
  private allRows: ActivityReportRow[] = [];

  constructor(
    private readonly router: Router,
    private readonly locationService: LocationService,
    private readonly reportService: ActivityReportService,
  ) {}

  ngOnInit(): void {
    const token = sessionStorage.getItem('token');
    if (!token) {
      void this.router.navigate(['/'], { replaceUrl: true });
      return;
    }

    // Figma: both range fields default to today's date; staff can change either.
    const today = toReportDateInputValue(new Date());
    this.customFromDate = today;
    this.customToDate = today;
    this.syncDraftFromActive();

    this.locationService.getActiveLocations().subscribe({
      next: (response) => {
        this.centers = response.locations ?? [];
        this.loadReport();
      },
      error: () => {
        this.centers = [];
        this.loadReport();
      },
    });
  }

  goBack(): void {
    void this.router.navigate(['/manage-services/senior-center/reports']);
  }

  get showProvidedBy(): boolean {
    return this.reportMode === 'delivered';
  }

  setReportMode(mode: ActivityReportMode): void {
    if (this.reportMode === mode) {
      return;
    }
    this.reportMode = mode;
    if (mode === 'booked') {
      this.filterProvidedBy = '';
      this.draftProvidedBy = '';
    }
    this.loadReport({ silent: true });
  }

  openFilterPanel(): void {
    this.syncDraftFromActive();
    this.isFilterPanelOpen = true;
  }

  closeFilterPanel(): void {
    this.isFilterPanelOpen = false;
  }

  resetFilterPanel(): void {
    this.draftActivityName = '';
    this.draftActivityType = '';
    this.draftProvidedBy = '';
    this.draftCenterName = '';
    this.draftFromDate = '';
    this.draftToDate = '';
  }

  applyFilterPanel(): void {
    this.filterActivityName = this.draftActivityName;
    this.filterActivityType = this.draftActivityType;
    this.filterProvidedBy = this.draftProvidedBy;
    this.filterCenterName = this.draftCenterName;

    if (this.draftFromDate && this.draftToDate) {
      this.customFromDate = this.draftFromDate;
      this.customToDate = this.draftToDate;
    }

    this.isFilterPanelOpen = false;
    this.loadReport({ silent: true });
  }

  onFiltersChanged(): void {
    this.loadReport({ silent: true });
  }

  onActivityFilterChanged(): void {
    this.applyClientFilters();
  }

  formatDate(value: string | null | undefined): string {
    return formatReportDateDisplay(value);
  }

  openDatePicker(event: Event): void {
    openNativeDatePicker(event);
  }

  formatDatePickerLabel(value: string | null | undefined): string {
    return formatReportDatePickerLabel(value);
  }

  onDownload(): void {
    if (!this.rows.length) {
      return;
    }

    const sheetRows = this.rows.map((row) => {
      const base: Record<string, string> = {
        'User Name': row.userName,
        Activity: row.activity,
        Type: row.type,
      };
      if (this.showProvidedBy) {
        base['Provided by'] = row.providedBy;
      }
      base['Center'] = row.center;
      base['Date'] = this.formatDate(row.participatedAt);
      return base;
    });

    const worksheet = XLSX.utils.json_to_sheet(sheetRows);
    const workbook = XLSX.utils.book_new();
    const sheetName = this.reportMode === 'booked' ? 'Activity Booked' : 'Activity Delivered';
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    XLSX.writeFile(workbook, `activity-report-${this.reportMode}-${this.downloadSuffix()}.xlsx`);
  }

  trackByRow(index: number, row: ActivityReportRow): string {
    return `${row.userName}|${row.activity}|${row.center}|${row.participatedAt ?? ''}|${index}`;
  }

  private syncDraftFromActive(): void {
    this.draftActivityName = this.filterActivityName;
    this.draftActivityType = this.filterActivityType;
    this.draftProvidedBy = this.filterProvidedBy;
    this.draftCenterName = this.filterCenterName;
    this.draftFromDate = this.customFromDate;
    this.draftToDate = this.customToDate;
  }

  private downloadSuffix(): string {
    return buildReportDownloadSuffix({
      timeframe: this.timeframe,
      selectedYear: '',
      selectedMonthNumber: '',
      customFromDate: this.customFromDate,
      customToDate: this.customToDate,
    });
  }

  private applyClientFilters(): void {
    let filtered = [...this.allRows];

    if (this.filterActivityName) {
      const want = this.filterActivityName.trim().toLowerCase();
      filtered = filtered.filter((row) => (row.activity || '').trim().toLowerCase() === want);
    }
    if (this.filterActivityType) {
      const want = this.filterActivityType.trim().toLowerCase();
      filtered = filtered.filter((row) => (row.type || '').trim().toLowerCase() === want);
    }
    if (this.filterProvidedBy) {
      const want = this.filterProvidedBy.trim().toLowerCase();
      filtered = filtered.filter((row) => (row.providedBy || '').trim().toLowerCase() === want);
    }

    this.rows = filtered;
  }

  private refreshFilterOptions(): void {
    const activities = new Set<string>();
    const types = new Set<string>();
    const providers = new Set<string>();
    for (const row of this.allRows) {
      const activity = (row.activity || '').trim();
      if (activity && activity !== '—') {
        activities.add(activity);
      }
      const type = (row.type || '').trim();
      if (type && type !== '—') {
        types.add(type);
      }
      const providedBy = (row.providedBy || '').trim();
      if (providedBy && providedBy !== '—') {
        providers.add(providedBy);
      }
    }
    const sortFn = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });
    this.activityOptions = Array.from(activities).sort(sortFn);
    this.activityTypeOptions = Array.from(types).sort(sortFn);
    this.providedByOptions = Array.from(providers).sort(sortFn);

    if (this.filterActivityName && !activities.has(this.filterActivityName)) {
      this.filterActivityName = '';
    }
    if (this.filterActivityType && !types.has(this.filterActivityType)) {
      this.filterActivityType = '';
    }
    if (this.filterProvidedBy && !providers.has(this.filterProvidedBy)) {
      this.filterProvidedBy = '';
    }
  }

  private loadReport(options: { silent?: boolean } = {}): void {
    const range = resolveReportDateRange({
      timeframe: this.timeframe,
      selectedYear: '',
      selectedMonthNumber: '',
      customFromDate: this.customFromDate,
      customToDate: this.customToDate,
    });
    if (!range) {
      this.errorMessage = 'Select a valid date range.';
      this.isInitialLoading = false;
      return;
    }

    if (!options.silent) {
      this.isInitialLoading = true;
    } else {
      this.isRefreshing = true;
    }
    this.errorMessage = null;

    this.reportService
      .getReport({
        fromDate: range.fromDate,
        toDate: range.toDate,
        center: this.filterCenterName || null,
        activity: this.filterActivityName || null,
        mode: this.reportMode,
      })
      .subscribe({
        next: (response) => {
          this.allRows = response.rows ?? [];
          this.refreshFilterOptions();
          this.applyClientFilters();
          this.isInitialLoading = false;
          this.isRefreshing = false;
        },
        error: () => {
          this.allRows = [];
          this.rows = [];
          this.errorMessage = 'Unable to load activity report.';
          this.isInitialLoading = false;
          this.isRefreshing = false;
        },
      });
  }
}
